// src/components/UserDetails.jsx
import React, { useState, useEffect } from "react";
import {
  TextField,
  Box,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import axios from "axios";

const API_BASE_URL = "https://formbuilderapi.getforsite.com";

export default function UserDetails({
  data,
  onChange,
  isEditable,
  styles,
  setUserId,
  setCompanyId,
  multiCompanies = false,
  userId,
  preselectedCompanyId = null,
}) {
  const [emailError, setEmailError] = useState("");
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [loading, setLoading] = useState(false);
  const [autoInitialized, setAutoInitialized] = useState(false);

  // 1) Autofill from URL userId
  useEffect(() => {
    if (!userId || autoInitialized) return;
    setLoading(true);

    axios
      .post(`${API_BASE_URL}/api/details`, {
        userId: Number(userId),
        multiCompany: multiCompanies,
      })
      .then(({ data: resp }) => {
        // NOTE: your server returns `firstName` / `lastName` (camelCased),
        // so you need to use resp.firstName, not resp.first_name
        onChange("firstName", resp.firstName);
        onChange("lastName", resp.lastName);

        if (multiCompanies && Array.isArray(resp.companies)) {
          setCompanies(resp.companies);
        } else if (!multiCompanies && typeof resp.companyId === "number") {
          setCompanies([
            { companyId: resp.companyId, name: resp.companyName || "" },
          ]);
        }

        setUserId(userId);
      })
      .catch((err) => console.error("Autofill failed:", err))
      .finally(() => {
        setAutoInitialized(true);
        setLoading(false);
      });
  }, [userId, multiCompanies, autoInitialized]);

  // 2) Once companies arrive, select the preselected company if provided, else the first
  useEffect(() => {
    if (!initialized && companies.length > 0) {
      setInitialized(true);
      const match = preselectedCompanyId
        ? companies.find((c) => c.companyId === preselectedCompanyId)
        : null;
      const toSelect = match || companies[0];
      setSelectedCompanyId(toSelect.companyId);
      setCompanyId(toSelect.companyId);
      onChange("company", toSelect.name);
    }
  }, [companies, initialized, setCompanyId, onChange, preselectedCompanyId]);

  // 3) If upstream data.company changes, sync back the select
  useEffect(() => {
    if (data.company && companies.length > 0) {
      const match = companies.find((c) => c.name === data.company);
      if (match && match.companyId !== selectedCompanyId) {
        setSelectedCompanyId(match.companyId);
      }
    }
  }, [data.company, companies, selectedCompanyId]);

  // 4) Email confirmation handler
  const handleConfirmEmail = async () => {
    const email = data.email?.trim();
    if (!email) {
      setEmailError("Please enter an email address.");
      return;
    }

    try {
      const url = multiCompanies
        ? "/api/confirm-email/companies"
        : "/api/confirm-email";
      const response = await axios.post(`${API_BASE_URL}${url}`, { email });
      const resp = response.data;

      if (resp.userId) {
        setUserId(resp.userId);
      }

      if (multiCompanies) {
        setCompanies(resp.companies || []);
      } else {
        setCompanies([
          { companyId: resp.companyId, name: resp.companyName || "" },
        ]);
      }

      setEmailConfirmed(true);
      setEmailError("");
    } catch (err) {
      setEmailError(err.response?.data?.error || "Error confirming email.");
      setEmailConfirmed(false);
      console.error("Error confirming email:", err);
    }
  };

  // 5) While autofill is loading, show spinner overlay
  if (loading) {
    return (
      <Box
        sx={{
          p: 2,
          my: 2,
          border: "1px solid #ddd",
          borderRadius: 1,
          textAlign: "center",
          backgroundColor: "#fafafa",
        }}
      >
        <CircularProgress size={24} />
        <Typography component="span" sx={{ ml: 1 }}>
          Autofill in progress…
        </Typography>
      </Box>
    );
  }

  // 6) Render normal fields
  return (
    <Box marginY={2}>
      <Grid container spacing={2}>
        {/* First Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="First Name"
            value={data.firstName || ""}
            onChange={(e) => onChange("firstName", e.target.value)}
            fullWidth
            required
            disabled={!isEditable}
          />
        </Grid>

        {/* Last Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Last Name"
            value={data.lastName || ""}
            onChange={(e) => onChange("lastName", e.target.value)}
            fullWidth
            required
            disabled={!isEditable}
          />
        </Grid>

        {/* Email */}
        <Grid item xs={12}>
          <TextField
            label="Email"
            type="email"
            value={data.email || ""}
            onChange={(e) => onChange("email", e.target.value)}
            fullWidth
            required
            disabled={!isEditable || emailConfirmed}
          />
        </Grid>

        {/* Confirm Email Button */}
        {isEditable && !userId && (
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleConfirmEmail}
              disabled={emailConfirmed}
              sx={{
                backgroundColor: styles.buttonColor,
                "&:hover": {
                  backgroundColor: styles.buttonColor,
                },
              }}
            >
              {emailConfirmed ? "Confirmed" : "Confirm Email"}
            </Button>
            {emailError && (
              <Typography color="error" sx={{ mt: 1 }}>
                {emailError}
              </Typography>
            )}
          </Grid>
        )}

        {/* Company */}
        <Grid item xs={12}>
          {multiCompanies && companies.length > 1 ? (
            <FormControl fullWidth disabled={!isEditable}>
              <InputLabel id="company-select-label">Company</InputLabel>
              <Select
                labelId="company-select-label"
                value={selectedCompanyId}
                label="Company"
                onChange={(e) => {
                  let newId = Number(e.target.value);
                  setSelectedCompanyId(newId);
                  const sel = companies.find((c) => c.companyId === newId);
                  if (sel) {
                    setCompanyId(sel.companyId);
                    onChange("company", sel.name);
                  }
                }}
              >
                {companies.map((c) => (
                  <MenuItem key={c.companyId} value={c.companyId}>
                    <Checkbox checked={selectedCompanyId === c.companyId} />
                    <ListItemText primary={c.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              label="Company"
              value={companies[0]?.name || data.company || ""}
              onChange={(e) => {
                onChange("company", e.target.value);
                setCompanyId(companies[0]?.companyId);
              }}
              fullWidth
              disabled={!isEditable}
            />
          )}
        </Grid>

        {/* Phone Number */}
        <Grid item xs={12}>
          <TextField
            label="Phone Number"
            type="tel"
            value={data.phoneNumber || ""}
            onChange={(e) => onChange("phoneNumber", e.target.value)}
            fullWidth
            disabled={!isEditable}
          />
        </Grid>
      </Grid>
    </Box>
  );
}